import { Component, computed, DestroyRef, inject, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Environment } from '../../core/models/environment.model';
import { EventMessageType } from '../../core/models/event-message-type.enum';
import { EventMessage } from '../../core/models/event-message.model';
import { Mode } from '../../core/models/mode.model';
import {
  anomalyTiresMenuItem,
  damageReportsMenuItem,
  dashboardMenuItem,
  MenuItem,
  MenuItemCounter,
  ModeCounter,
  myBatchesMenuItem,
  myTiresMenuItem,
  newContainerMenuItem,
  newNotificationMenuItem,
  newPreparationMenuItem,
  rejectedTiresMenuItem,
  resetDataMenuItem,
} from '../../core/models/ui/menu/menu-item.model';
import { EnvironmentService } from '../../core/services/environment.service';
import { DemoService } from '../../core/services/demo.service';
import { ModeService } from '../../core/services/mode.service';
import { NotificationsService } from '../../core/services/notifications.service';
import { SseEventsService } from '../../core/services/sse-events.service';
import { BsModalService, PageMenuTemplateComponent, SideMenuItemComponent } from 'bs-components';
import { ResetDataModalComponent } from './reset-data-modal/reset-data-modal.component';

import { TranslatePipe } from '@ngx-translate/core';
import { ThemeService } from '../../core/services/theme.service';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-workflow',
  templateUrl: './workflow.component.html',
  styleUrls: ['./workflow.component.scss'],
  imports: [PageMenuTemplateComponent, SideMenuItemComponent, TranslatePipe]
})
export class WorkflowComponent implements OnInit {
  private readonly modeService = inject(ModeService);
  private readonly sseEvents = inject(SseEventsService);
  private readonly demoService = inject(DemoService);
  private readonly modalService = inject(BsModalService);
  private readonly notificationsService = inject(NotificationsService);
  private readonly themeService = inject(ThemeService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly destroyRef = inject(DestroyRef);


  public readonly modeMenuItems$: Signal<MenuItem[]> = computed(() => {
    return this.toModeMenuItems(this.modeService.currentEnvModes$(), this.modeCounters$());
  });

  public readonly currentCustomer$: Signal<Environment | undefined> = this.environmentService.currentEnvironment$;

  public readonly rejectedTiresMenuItem: MenuItem = rejectedTiresMenuItem;
  public readonly anomalyTires: MenuItem = anomalyTiresMenuItem;
  public readonly damageReportsMenuItem: MenuItem = damageReportsMenuItem;
  public readonly myBatchesMenuItem: MenuItem = myBatchesMenuItem;
  public readonly myTiresMenuItem: MenuItem = myTiresMenuItem;
  public readonly dashboardMenuItem: MenuItem = dashboardMenuItem;
  public readonly resetDataMenuItem: MenuItem = resetDataMenuItem;
  public readonly newContainerMenuItem: MenuItem = newContainerMenuItem;
  public readonly newPreparationMenuItem: MenuItem = newPreparationMenuItem;
  public readonly newNotificationMenuItem: MenuItem = newNotificationMenuItem;

  private readonly modeCounters$: WritableSignal<ModeCounter[]> = signal([]);

  ngOnInit(): void {
    const batchCounterEvent$ = this.sseEvents
      .on(EventMessageType.BATCH_COUNTER)
      .pipe(takeUntilDestroyed(this.destroyRef));

    batchCounterEvent$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((event) => this.modeCounters$.set((event as EventMessage)?.data))
      )
      .subscribe();


    this.subscribeToCounterEvent(EventMessageType.DAMAGE_REPORTS_COUNTER_EVENT, this.damageReportsMenuItem);
    this.subscribeToCounterEvent(EventMessageType.REJECTED_TYRE_COUNTER_EVENT, this.rejectedTiresMenuItem);
    this.subscribeToCounterEvent(EventMessageType.DOUBLE_SN_TYRE_COUNTER_EVENT, this.anomalyTires);
  }

  private toModeMenuItem(m: Mode, counters: ModeCounter[]): MenuItem {
    const itemCounters: MenuItemCounter[] = m.states
      .flatMap<MenuItemCounter | undefined>((s) => {
        return counters?.map((counter: ModeCounter) => {
          if (counter.state == s.id) {
            return {
              value: counter.value,
              color: this.themeService.isDark$() ? s.color : s.colorLight,
            };
          }
        });
      })
      .filter((c) => c != undefined);

    return {
      id: MenuItem.getId(m.name),
      url: '/workflow/' + m.urlName + '/batches',
      icon: this.getMenuItemIcon(m),
      label: m.name,
      tooltip: m.name,
      counters: itemCounters || [],
      order: m.order,
      exactPath: false,
    };
  }

  private subscribeToCounterEvent(eventType: EventMessageType, menuItem: MenuItem): Subscription {
    return this.sseEvents
      .on(eventType)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((event) => {
          if (event) {
            menuItem.counters![0].value = event.data;
            menuItem.counters = [...menuItem.counters!];
          }
        })
      )
      .subscribe();
  }

  private getMenuItemIcon(mode: Mode): string {
    return mode.incoming ? 'fas fa-sign-in-alt' : 'fas fa-sign-out-alt';
  }

  public handleResetDataMenuClick(): void {
    this.modalService.open(ResetDataModalComponent);
  }

  public handleAddNewContainer(): void {
    this.demoService.addNewContainer();
  }

  public handleAddNewPreparation(): void {
    this.demoService.addNewPreparation();
  }

  public handleAddNewNotification(): void {
    this.notificationsService.generateFakeNotification().subscribe();
  }

  public get isProduction(): boolean {
    return environment.production;
  }

  private toModeMenuItems(modes: Mode[], counters: ModeCounter[]) {
    return modes
      .filter((mode) => mode.config.enabled)
      .map((m) => this.toModeMenuItem(m, counters))
      .sort((a: any, b: any) => {
        if (a.order > b.order) {
          return 1;
        } else if (a.order < b.order) {
          return -1;
        } else {
          return 0;
        }
      });
  };
}
